import { useMemo, useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../utils/i18n';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import dayjs from 'dayjs';
import DetailDialog from '../../../components/DetailDialog';
import EvidenceDialog from './EvidenceDialog';
import RetaseDialog from './RetaseDialog';
import FormulaDialog from './FormulaDialog';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { determineIndexNameWithFallback, EVENT_SOURCE_MAP, getEventSource } from '../../../helpers/howenScoreHelper';

export { EVENT_SOURCE_MAP, getEventSource };

const EVENT_KEY_I18N_MAP = {
  over_speed: 'driverScore.eventOverSpeed',
  idle_time: 'driverScore.eventIdleTime',
  harsh_braking: 'driverScore.eventHarshBraking',
  harsh_acceleration: 'driverScore.eventHarshAcceleration',
  yawning: 'driverScore.eventYawning',
  smoking: 'driverScore.eventSmoking',
  sleepy: 'driverScore.eventSleepy',
  distraction: 'driverScore.eventDistraction',
  phone_call: 'driverScore.eventPhoneCall',
  covering_camera: 'driverScore.eventCoveringCamera'
};

// Helper function to format event keys to title case or i18n
const formatEventKey = (key, t) => {
  if (!key) return '';
  const fallback = key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const i18nKey = EVENT_KEY_I18N_MAP[key];
  if (i18nKey && typeof t === 'function') {
    return t(i18nKey, fallback);
  }
  return fallback;
};

function calculateEventScore(eventCount, totalRows, weight) {
  if (weight === 0) return 0;
  if (!totalRows || totalRows === 0) return 10; // full score kalau belum ada data sama sekali
  let ratio = Number(eventCount || 0) / Number(totalRows);
  if (ratio > 1) ratio = 1;
  return (1 - ratio) * 10; // max score selalu 10
}

const getScoreColor = (score) => {
  const numScore = Number(score) || 0;
  if (numScore >= 8) return '#2e7d32';
  if (numScore >= 5) return '#ff9800';
  return '#f44336';
};

export const getSourceColor = (source) => {
  switch (source) {
    case 'GPS':
      return { bgcolor: '#e3f2fd', color: '#1565c0', label: 'GPS Telematik' };
    case 'VSS':
      return { bgcolor: '#f3e5f5', color: '#6a1b9a', label: 'VSS Camera' };
    default:
      return { bgcolor: '#f5f5f5', color: '#616161', label: source };
  }
};

export const getIndexColor = (indexName) => {
  switch (String(indexName).toUpperCase()) {
    case 'A':
      return { bgcolor: '#e8f5e9', color: '#2e7d32' };
    case 'B':
      return { bgcolor: '#f0f4c3', color: '#827717' };
    case 'C':
      return { bgcolor: '#fff3e0', color: '#e65100' };
    case 'D':
      return { bgcolor: '#ffebee', color: '#c62828' };
    default:
      return { bgcolor: '#f5f5f5', color: '#616161' };
  }
};

const isRetaseDayRow = (dayRow) => Array.isArray(dayRow?.retase?.retase) && dayRow.retase.retase.length > 0;

export default function DetailDriverScoreDialog({ open, onClose, data }) {
  const { t } = useLanguage(); // DetailDriverScoreDialog
  const [evidenceModal, setEvidenceModal] = useState(null);
  const [retaseModal, setRetaseModal] = useState(null);
  const [formulaModal, setFormulaModal] = useState(null);
  const [showTotalRows, setShowTotalRows] = useState(false);

  const roundedLabel = (dir) => (dir === 'up' ? t('driverScore.roundedUp', 'rounded up') : t('driverScore.roundedDown', 'rounded down'));

  const retaseEvents = useMemo(() => (data?.events_retase || []).filter((ev) => ev.weight != null && Number(ev.weight) > 0), [data]);
  const nonRetaseEvents = useMemo(() => (data?.events_non_retase || []).filter((ev) => ev.weight != null && Number(ev.weight) > 0), [data]);
  const combineEvents = useMemo(() => (data?.events || []).filter((ev) => ev.weight != null && Number(ev.weight) > 0), [data]);

  const availableScoreTabs = useMemo(() => {
    const tabs = [];
    if (retaseEvents.length > 0 || data?.total_score_retase != null) tabs.push({ key: 'retase', label: t('driverScore.retase', 'Retase') });
    if (nonRetaseEvents.length > 0 || data?.total_score_non_retase != null)
      tabs.push({ key: 'non_retase', label: t('driverScore.nonRetase', 'Non Retase') });
    if (
      (combineEvents.length > 0 || data?.total_score != null) &&
      data?.total_score_retase != null &&
      data?.total_score_non_retase != null
    ) {
      tabs.push({ key: 'combine', label: t('driverScore.combine', 'Combine') });
    }
    return tabs;
  }, [retaseEvents, nonRetaseEvents, combineEvents, data, t]);

  const [scoreTab, setScoreTab] = useState(null);

  useEffect(() => {
    if (availableScoreTabs.length > 0) {
      if (!availableScoreTabs.some((t) => t.key === scoreTab)) {
        setScoreTab(availableScoreTabs[0].key);
      }
    } else {
      setScoreTab(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableScoreTabs, data]);

  const matchesTab = useCallback(
    (dayRow) => {
      if (scoreTab === 'retase') return isRetaseDayRow(dayRow);
      if (scoreTab === 'non_retase') return !isRetaseDayRow(dayRow);
      return true;
    },
    [scoreTab]
  );

  const gpsEvidenceByKey = useMemo(() => {
    const result = {};
    const allEventHolders = [];
    if (Array.isArray(data?._dailyBreakdown) && data._dailyBreakdown.length > 0) {
      data._dailyBreakdown.forEach((dayRow) => {
        if (!matchesTab(dayRow)) return;
        if (Array.isArray(dayRow.events)) {
          allEventHolders.push(...dayRow.events);
        }
      });
    } else if (matchesTab(data) && Array.isArray(data?.events)) {
      allEventHolders.push(...data.events);
    }

    allEventHolders.forEach((ev) => {
      const k = ev.event_key;
      if (!k || !Array.isArray(ev.evidence) || ev.evidence.length === 0) return;
      if (!result[k]) result[k] = {};

      ev.evidence.forEach((item) => {
        const startTime = item.start_time || item.time || item.event_time;
        if (!startTime) return;
        const dateKey = dayjs(startTime).format('DD MMM YYYY');
        if (!result[k][dateKey]) result[k][dateKey] = [];

        const dedupeKey = `${startTime}_${item.lat}_${item.lon}`;
        const exists = result[k][dateKey].some(
          (existing) => `${existing.start_time || existing.time || existing.event_time}_${existing.lat}_${existing.lon}` === dedupeKey
        );
        if (!exists) {
          result[k][dateKey].push(item);
        }
      });
    });

    Object.keys(result).forEach((k) => {
      Object.keys(result[k]).forEach((dateKey) => {
        result[k][dateKey].sort((a, b) => new Date(a.start_time || a.time) - new Date(b.start_time || b.time));
      });
    });

    return result;
  }, [data, matchesTab]);

  // Group VSS evidence by event_key and date
  const vssEvidenceByKey = useMemo(() => {
    const result = {};
    const allViolations = [];
    if (Array.isArray(data?._dailyBreakdown) && data._dailyBreakdown.length > 0) {
      data._dailyBreakdown.forEach((dayRow) => {
        if (!matchesTab(dayRow)) return;
        if (Array.isArray(dayRow._violationEvents)) {
          allViolations.push(...dayRow._violationEvents);
        }
      });
    } else if (matchesTab(data) && Array.isArray(data?._violationEvents)) {
      allViolations.push(...data._violationEvents);
    }

    allViolations.forEach((item) => {
      const k = item.event_key;
      const eventTime = item.event_time || item.time;
      if (!k || !eventTime) return;
      if (!result[k]) result[k] = {};

      const dateKey = dayjs(eventTime).format('DD MMM YYYY');
      if (!result[k][dateKey]) result[k][dateKey] = [];

      const dedupeKey = `${eventTime}_${item.vehicle}_${item.violation}`;
      const exists = result[k][dateKey].some(
        (existing) => `${existing.event_time || existing.time}_${existing.vehicle}_${existing.violation}` === dedupeKey
      );
      if (!exists) {
        result[k][dateKey].push(item);
      }
    });

    Object.keys(result).forEach((k) => {
      Object.keys(result[k]).forEach((dateKey) => {
        result[k][dateKey].sort((a, b) => new Date(a.event_time || a.time) - new Date(b.event_time || b.time));
      });
    });

    return result;
  }, [data, matchesTab]);

  const getEvidenceCount = (eventKey, source) => {
    const grouped = source === 'GPS' ? gpsEvidenceByKey[eventKey] : vssEvidenceByKey[eventKey];
    if (!grouped) return 0;
    return Object.values(grouped).reduce((total, items) => total + (Array.isArray(items) ? items.length : 0), 0);
  };

  const dailyViolationSummary = useMemo(() => {
    const rawRows =
      Array.isArray(data?._dailyBreakdown) && data._dailyBreakdown.length > 0 ? [...data._dailyBreakdown] : data ? [data] : [];

    const dailyRows = rawRows.filter((dayRow) => matchesTab(dayRow));
    dailyRows.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    const groups = [];
    dailyRows.forEach((dayRow) => {
      // Include all events with weight > 0 (even if count is 0)
      const violations = (dayRow.events || [])
        .filter((ev) => ev.weight != null && Number(ev.weight) > 0)
        .sort((a, b) => Number(b.event_count || 0) - Number(a.event_count || 0));

      if (violations.length > 0) {
        const scoreIndexes =
          dayRow.score_indexes ||
          dayRow.idx_detail ||
          dayRow.score_config?.score_indexes ||
          data?.score_indexes ||
          data?.idx_detail ||
          data?.score_config?.score_indexes ||
          data?._dailyBreakdown?.[0]?.score_indexes ||
          data?._dailyBreakdown?.[0]?.idx_detail ||
          data?._dailyBreakdown?.[0]?.score_config?.score_indexes ||
          [];

        const pctScore = dayRow.pct_score != null ? dayRow.pct_score : dayRow.total_score != null ? dayRow.total_score : null;
        let idxName = dayRow.idx_name || '-';
        let idxIsEstimated = false;
        let idxRoundedDirection = null;

        if (pctScore != null) {
          const idxResult = determineIndexNameWithFallback(pctScore, scoreIndexes);
          if (idxResult.idx_name !== '-') {
            idxName = idxResult.idx_name;
            idxIsEstimated = idxResult.isEstimated;
            idxRoundedDirection = idxResult.roundedDirection;
          }
        }

        groups.push({
          startDate: dayRow.start_date,
          vehicle: dayRow.license_plate || dayRow.vehicle_name || dayRow.vehicle || data?.license_plate || data?.vehicle_name || '-',
          scoreConfigName: dayRow.score_config_name || data?.score_config_name || '-',
          idxName,
          idxIsEstimated,
          idxRoundedDirection,
          pctScore,
          idxNameRetase: dayRow.idx_name_retase || dayRow.idx_name || '-',
          pctScoreRetase: dayRow.pct_score_retase != null ? dayRow.pct_score_retase : null,
          idxNameNonRetase: dayRow.idx_name_non_retase || dayRow.idx_name || '-',
          pctScoreNonRetase: dayRow.pct_score_non_retase != null ? dayRow.pct_score_non_retase : null,
          scoreIndexes,
          totalWaypoints: Number(dayRow.total_waypoints ?? dayRow.total_rows ?? 0),
          vssTotalRows: dayRow.vssTotalRows ?? data?.vssTotalRows ?? null,
          violations,
          isRetaseDay: isRetaseDayRow(dayRow)
        });
      }
    });

    return groups;
  }, [data, matchesTab]);

  const getRetaseForDate = (dateKey) => {
    if (!dateKey) return { trips: [], geofences: [], transitPoints: [] };
    const allTrips = [];
    const geofenceMap = new Map();
    const transitPointMap = new Map();

    const processDayRow = (dayRow) => {
      if (!dayRow) return;
      const rowDateKey = dayRow.start_date ? dayjs(dayRow.start_date).format('DD MMM YYYY') : '';
      if (rowDateKey && rowDateKey !== dateKey) return;

      // 1. Trips from retase.route_sort or route_sort
      if (Array.isArray(dayRow.retase?.route_sort)) {
        allTrips.push(...dayRow.retase.route_sort);
      } else if (Array.isArray(dayRow.route_sort)) {
        allTrips.push(...dayRow.route_sort);
      }

      // 2. Geofences & Transit Points from events
      if (Array.isArray(dayRow.events)) {
        dayRow.events.forEach((ev) => {
          const geos = ev.geofences || ev.geofence;
          if (Array.isArray(geos)) {
            geos.forEach((g) => {
              const key = g.id != null ? String(g.id) : `${g.name}_${g.geofence_code}`;
              if (key && !geofenceMap.has(key)) {
                geofenceMap.set(key, { ...g, spatial_type: 'geofence' });
              }
            });
          }

          const transits = ev.transit_points || ev.transitpoints || ev.transit_point || ev.transitPoints;
          if (Array.isArray(transits)) {
            transits.forEach((tp) => {
              const key = tp.id != null ? String(tp.id) : `${tp.name}_${tp.code}`;
              if (key && !transitPointMap.has(key)) {
                transitPointMap.set(key, { ...tp, spatial_type: 'transit_point' });
              }
            });
          }
        });
      }

      // Also check if geofences / transit_points are directly in dayRow or dayRow.retase
      const directGeos = dayRow.geofences || dayRow.retase?.geofences || dayRow.retase?.geofence;
      if (Array.isArray(directGeos)) {
        directGeos.forEach((g) => {
          const key = g.id != null ? String(g.id) : `${g.name}_${g.geofence_code}`;
          if (key && !geofenceMap.has(key)) {
            geofenceMap.set(key, { ...g, spatial_type: 'geofence' });
          }
        });
      }

      const directTransits = dayRow.transit_points || dayRow.transitpoints || dayRow.retase?.transit_points || dayRow.retase?.transitpoints;
      if (Array.isArray(directTransits)) {
        directTransits.forEach((tp) => {
          const key = tp.id != null ? String(tp.id) : `${tp.name}_${tp.code}`;
          if (key && !transitPointMap.has(key)) {
            transitPointMap.set(key, { ...tp, spatial_type: 'transit_point' });
          }
        });
      }
    };

    if (Array.isArray(data?._dailyBreakdown) && data._dailyBreakdown.length > 0) {
      data._dailyBreakdown.forEach((dayRow) => {
        processDayRow(dayRow);
      });
    } else if (data) {
      processDayRow(data);
    }

    const trips = allTrips
      .filter((trip) => {
        const start = trip.details?.[0]?.start || trip.start || trip.start_time;
        return !start || dayjs(start).format('DD MMM YYYY') === dateKey;
      })
      .sort((a, b) => {
        const timeA = a.details?.[0]?.start ? new Date(a.details[0].start).getTime() : 0;
        const timeB = b.details?.[0]?.start ? new Date(b.details[0].start).getTime() : 0;
        return timeA - timeB;
      });

    const geofences = Array.from(geofenceMap.values());
    const transitPoints = Array.from(transitPointMap.values());

    return { trips, geofences, transitPoints };
  };

  const renderEventGrid = (events) => (
    <Grid container spacing={2}>
      {events.map((ev, index) => {
        const actualSource = getEventSource(ev.event_key);
        const isVssEvent = ['sleepy', 'yawning', 'phone_call', 'distraction', 'smoking', 'covering_camera'].includes(ev.event_key);
        const totalRows = Number(
          (isVssEvent ? (data?.vssTotalRows ?? ev.total_rows) : ev.total_rows) ?? data?.total_waypoints ?? data?.total_rows ?? 0
        );
        let calculatedScore = 0;

        // 1. Override: Jika total_rows === 0 (tidak ada data / belum ada perjalanan), paksa score = 10 (full score).
        if (totalRows === 0) {
          calculatedScore = 10;
        } else if (actualSource === 'GPS') {
          calculatedScore = ev.score != null ? Number(ev.score) : 0;
        } else {
          calculatedScore = ev.score != null ? Number(ev.score) : calculateEventScore(ev.event_count || 0, totalRows, ev.weight);
        }

        const displayScore = Number.isInteger(calculatedScore) ? calculatedScore : parseFloat(calculatedScore.toFixed(2));
        const scoreIndexes =
          data?.score_indexes ||
          data?.idx_detail ||
          data?.score_config?.score_indexes ||
          data?._dailyBreakdown?.[0]?.score_indexes ||
          data?._dailyBreakdown?.[0]?.idx_detail ||
          data?._dailyBreakdown?.[0]?.score_config?.score_indexes ||
          [];
        const eventIndexResult = determineIndexNameWithFallback(calculatedScore * 10, scoreIndexes);
        const eventIndex = eventIndexResult.idx_name;
        const eventIndexIsEstimated = eventIndexResult.isEstimated;
        const eventIndexRoundedDirection = eventIndexResult.roundedDirection;
        const evidenceCount = getEvidenceCount(ev.event_key, actualSource);

        return (
          <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 180,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                }
              }}
            >
              <Box sx={{ mb: 1, minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="subtitle1" fontWeight="600">
                  {formatEventKey(ev.event_key, t)}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: eventIndexIsEstimated ? 0.5 : 1 }}>
                <Tooltip title={t('driverScore.clickToSeeFormula', 'Klik untuk lihat rumus & detail perhitungan')} arrow>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    onClick={() =>
                      setFormulaModal({
                        eventKey: ev.event_key,
                        eventCount: ev.event_count || 0,
                        totalRows,
                        weight: ev.weight || 0,
                        score: calculatedScore,
                        eventIndex,
                        eventIndexIsEstimated,
                        eventIndexRoundedDirection,
                        dateKey: '',
                        vehicle: data?.license_plate || data?.vehicle_name || data?.vehicle || '-',
                        scoreConfigName: data?.score_config_name || '-',
                        isRetaseDay: scoreTab === 'retase',
                        groupViolations: events,
                        groupPctScore: activeScoreInfo?.score !== '-' ? activeScoreInfo?.score : null,
                        groupIdxName: activeScoreInfo?.idx || '-',
                        scoreIndexes,
                        vssTotalRows: data?.vssTotalRows ?? null,
                        fallbackWaypoints: Number(data?.total_waypoints ?? data?.total_rows ?? 0)
                      })
                    }
                    sx={{
                      color: getScoreColor(calculatedScore),
                      cursor: 'pointer',
                      borderBottom: '1px dashed currentColor',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    {displayScore}
                  </Typography>
                </Tooltip>
                <Tooltip
                  title={
                    eventIndexIsEstimated
                      ? `${t('driverScore.indexWord', 'Index')} ${eventIndex} (${roundedLabel(eventIndexRoundedDirection)} ${t('driverScore.fromScore', 'from score')} ${(calculatedScore * 10).toFixed(2)})`
                      : ''
                  }
                  arrow
                  disableHoverListener={!eventIndexIsEstimated}
                >
                  <Chip
                    label={eventIndex || '-'}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 32,
                      ...getIndexColor(eventIndex)
                    }}
                  />
                </Tooltip>
              </Stack>

              {eventIndexIsEstimated && (
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontSize: '0.68rem',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    mb: 0.5
                  }}
                >
                  *{roundedLabel(eventIndexRoundedDirection)}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" fontWeight="500">
                {t('driverScore.maxScoreLabel', 'Max Score')}: 10 | {t('driverScore.countLabel', 'Count')}: {ev.event_count || 0}
              </Typography>

              {evidenceCount > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setEvidenceModal({ eventKey: ev.event_key, source: actualSource })}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('driverScore.evidence', 'Evidence')} ({evidenceCount})
                </Button>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const activeScoreInfo = useMemo(() => {
    const map = {
      retase: {
        label: t('driverScore.retase', 'Retase'),
        idx: data?.idx_name_retase || '-',
        score: data?.total_score_retase != null ? data.total_score_retase : '-',
        isEstimated: !!data?.idx_name_retase_is_estimated,
        roundedDirection: data?.idx_name_retase_rounded_direction
      },
      non_retase: {
        label: t('driverScore.nonRetase', 'Non Retase'),
        idx: data?.idx_name_non_retase || '-',
        score: data?.total_score_non_retase != null ? data.total_score_non_retase : '-',
        isEstimated: !!data?.idx_name_non_retase_is_estimated,
        roundedDirection: data?.idx_name_non_retase_rounded_direction
      },
      combine: {
        label: t('driverScore.combine', 'Combine'),
        idx: data?.idx_name || '-',
        score: data?.pct_score != null ? data.pct_score : data?.total_score != null ? data.total_score : '-',
        isEstimated: false,
        roundedDirection: null
      }
    };
    if (scoreTab && map[scoreTab]) return map[scoreTab];
    if (data?.total_score_retase != null) return map.retase;
    if (data?.total_score_non_retase != null) return map.non_retase;
    return map.combine;
  }, [scoreTab, data, t]);

  const activeMetrics = useMemo(() => {
    const map = {
      retase: {
        distance: data?.total_distance_km_retase != null ? data.total_distance_km_retase : '-',
        travelTime: data?.trip_duration_retase || data?.total_trip_duration_retase || '-',
        stopTime: data?.stop_duration_retase || data?.total_stop_duration_retase || '-'
      },
      non_retase: {
        distance: data?.total_distance_km_non_retase != null ? data.total_distance_km_non_retase : '-',
        travelTime: data?.trip_duration_non_retase || data?.total_trip_duration_non_retase || '-',
        stopTime: data?.stop_duration_non_retase || data?.total_stop_duration_non_retase || '-'
      },
      combine: {
        distance: data?.total_distance_km != null ? data.total_distance_km : '-',
        travelTime: data?.trip_duration || data?.total_trip_duration || '-',
        stopTime: data?.stop_duration || data?.total_stop_duration || '-'
      }
    };
    if (scoreTab && map[scoreTab]) return map[scoreTab];
    if (data?.total_score_retase != null || data?.total_distance_km_retase != null) return map.retase;
    if (data?.total_score_non_retase != null || data?.total_distance_km_non_retase != null) return map.non_retase;
    return map.combine;
  }, [scoreTab, data]);

  if (!data) return null;

  return (
    <>
      <DetailDialog
        open={open}
        onClose={onClose}
        title={t('driverScore.detailTitle', 'Detail Driver Score')}
        maxWidth="lg"
        fullScreenMode={true}
        icon={<InsertDriveFileIcon sx={{ color: '#fff', fontSize: 24 }} />}
      >
        <Box sx={{ flexGrow: 1, p: 1 }}>
          <Grid container spacing={1}>
            {/* Left Panel: Driver Info */}
            <Grid item size={{ xs: 12, md: 3 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 1,
                      bgcolor: 'primary.light',
                      fontSize: 64,
                      fontWeight: 'bold',
                      border: '4px solid #fff',
                      boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)'
                    }}
                  >
                    {data.driver_name ? data.driver_name.charAt(0).toUpperCase() : 'D'}
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold">
                    {data.driver_name || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('driverScore.driverCodeLabel', 'Driver Code')}: {data.driver_code || data.driver || '-'}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="500">
                        {t('driverScore.indexWord', 'Index')} ({activeScoreInfo.label})
                      </Typography>
                      <Tooltip
                        title={
                          activeScoreInfo.isEstimated
                            ? `${t('driverScore.indexWord', 'Index')} ${activeScoreInfo.idx} (${roundedLabel(activeScoreInfo.roundedDirection)} ${t('driverScore.fromScore', 'from score')} ${activeScoreInfo.score})`
                            : ''
                        }
                        arrow
                        disableHoverListener={!activeScoreInfo.isEstimated}
                      >
                        <Chip
                          label={activeScoreInfo.idx || '-'}
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            minWidth: 50,
                            ...getIndexColor(activeScoreInfo.idx)
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {activeScoreInfo.isEstimated && (
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'right',
                          fontSize: '0.68rem',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          mt: 0.25
                        }}
                      >
                        *{roundedLabel(activeScoreInfo.roundedDirection)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.scoreWord', 'Score')} ({activeScoreInfo.label})
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {activeScoreInfo.score != null ? activeScoreInfo.score : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.scoreConfig', 'Score Config')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" textAlign="right">
                      {data.score_config_name || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.minPct', 'Min Pct (%)')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.pct_warn_limit} %
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.minDistKm', 'Min Distance (KM)')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.min_dist_km} KM
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.totalMileage', 'Total Mileage (KM)')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {activeMetrics.distance}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.totalTravelTime', 'Total Travel Time')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {activeMetrics.travelTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('driverScore.totalStopTime', 'Total Stop Time')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {activeMetrics.stopTime}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Right Panel: Events Grouped by Retase, Non Retase & Combine Tabs */}
            <Grid item size={{ xs: 12, md: 9 }}>
              {availableScoreTabs.length > 0 && (
                <Tabs
                  value={scoreTab}
                  onChange={(e, val) => {
                    setScoreTab(val);
                    setEvidenceModal(null);
                  }}
                  sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 } }}
                >
                  {availableScoreTabs.map((tab) => (
                    <Tab key={tab.key} value={tab.key} label={tab.label} />
                  ))}
                </Tabs>
              )}

              {scoreTab === 'retase' && renderEventGrid(retaseEvents)}
              {scoreTab === 'non_retase' && renderEventGrid(nonRetaseEvents)}
              {scoreTab === 'combine' && renderEventGrid(combineEvents)}
            </Grid>

            {/* Bottom Section: Ringkasan Pelanggaran per Hari */}
            {dailyViolationSummary.length > 0 && (
              <Grid item size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
                  }}
                >
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t('driverScore.violationSummary', 'Violation Summary per Day')}
                    </Typography>
                    <FormControlLabel
                      control={<Switch size="small" checked={showTotalRows} onChange={(e) => setShowTotalRows(e.target.checked)} />}
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          {t('driverScore.showTotalRows', 'Show Total Rows')}
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                  </Box>
                  <TableContainer
                    sx={{
                      maxHeight: 560,
                      overflow: 'auto',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&::-webkit-scrollbar': {
                        width: 8,
                        height: 8
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.15)',
                        borderRadius: 2
                      }
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        minWidth: 1150,
                        borderCollapse: 'separate',
                        // Fix table layout and reset theme first-of-type / last-of-type padding on TableRow
                        '& .MuiTableCell-root': {
                          px: 1.5,
                          py: 1,
                          borderColor: 'divider'
                        },
                        '& .MuiTableRow-root .MuiTableCell-root:first-of-type': {
                          paddingLeft: '12px !important'
                        },
                        '& .MuiTableRow-root .MuiTableCell-root:last-of-type': {
                          paddingRight: '12px !important'
                        },
                        // Sticky header styling
                        '& .MuiTableCell-head': {
                          bgcolor: '#f8fafc',
                          fontWeight: 'bold',
                          color: 'text.primary',
                          fontSize: '0.75rem',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          zIndex: 3
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 110, width: 110, fontWeight: 'bold' }}>{t('driverScore.dateLabel', 'Date')}</TableCell>
                          <TableCell sx={{ minWidth: 95, width: 95, fontWeight: 'bold' }}>{t('driverScore.typeLabel', 'Type')}</TableCell>
                          <TableCell sx={{ minWidth: 100, width: 100, fontWeight: 'bold' }}>
                            {t('driverScore.vehicle', 'Vehicle')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 150, width: 150, fontWeight: 'bold' }}>
                            {t('driverScore.scoreConfig', 'Score Config')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 115, width: 115, fontWeight: 'bold' }}>
                            {t('driverScore.indexScore', 'Index Score')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 170, fontWeight: 'bold' }}>{t('driverScore.event', 'Event')}</TableCell>
                          <TableCell sx={{ minWidth: 70, width: 70, fontWeight: 'bold' }} align="center">
                            {t('driverScore.weight', 'Weight')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 105, width: 105, fontWeight: 'bold' }} align="right">
                            {t('driverScore.totalViolations', 'Total Violations')}
                          </TableCell>
                          {showTotalRows && (
                            <TableCell sx={{ minWidth: 90, width: 90, fontWeight: 'bold' }} align="center">
                              {scoreTab === 'retase' || scoreTab === 'combine'
                                ? t('driverScore.totalRowsByRetase', 'Total Rows By Retase')
                                : t('driverScore.totalRows', 'Total Rows')}
                            </TableCell>
                          )}
                          <TableCell sx={{ minWidth: 75, width: 75, fontWeight: 'bold' }} align="center">
                            {t('driverScore.scoreWord', 'Score')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 90, width: 90, fontWeight: 'bold' }} align="center">
                            {t('driverScore.indexEvent', 'Index Event')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 95, width: 95, fontWeight: 'bold' }} align="center">
                            {t('driverScore.evidence', 'Evidence')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 95, width: 95, fontWeight: 'bold' }} align="center">
                            {t('driverScore.retase', 'Retase')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyViolationSummary.map((group, gIdx) => {
                          const dateKey = group.startDate ? dayjs(group.startDate).format('DD MMM YYYY') : '';
                          const retaseDataForDate = getRetaseForDate(dateKey);
                          const retaseTripsForDate = retaseDataForDate.trips;
                          const isLastGroup = gIdx === dailyViolationSummary.length - 1;

                          return group.violations.map((ev, vIdx) => {
                            const isFirst = vIdx === 0;
                            const isLastInGroup = vIdx === group.violations.length - 1;
                            const source = getEventSource(ev.event_key);
                            const evidenceListForDate =
                              (source === 'GPS' ? gpsEvidenceByKey[ev.event_key]?.[dateKey] : vssEvidenceByKey[ev.event_key]?.[dateKey]) ||
                              [];
                            const hasEvidence = evidenceListForDate.length > 0;

                            const isVssEvent = ['sleepy', 'yawning', 'phone_call', 'distraction', 'smoking', 'covering_camera'].includes(
                              ev.event_key
                            );
                            const eventTotalRows = isVssEvent
                              ? (group.vssTotalRows ?? data?.vssTotalRows ?? ev.total_rows ?? 0)
                              : (ev.total_rows ?? '');
                            const totalRows = Number(
                              (isVssEvent ? (group.vssTotalRows ?? data?.vssTotalRows ?? ev.total_rows) : ev.total_rows) ??
                                group.totalWaypoints ??
                                data?.total_waypoints ??
                                data?.total_rows ??
                                0
                            );
                            let calculatedEventScore = 0;
                            if (totalRows === 0) {
                              calculatedEventScore = 10;
                            } else if (source === 'GPS') {
                              calculatedEventScore = ev.score != null ? Number(ev.score) : 0;
                            } else {
                              calculatedEventScore =
                                ev.score != null ? Number(ev.score) : calculateEventScore(ev.event_count || 0, totalRows, ev.weight);
                            }
                            const displayEventScore = Number.isInteger(calculatedEventScore)
                              ? calculatedEventScore
                              : parseFloat(calculatedEventScore.toFixed(2));
                            const eventIndexResult = determineIndexNameWithFallback(calculatedEventScore * 10, group.scoreIndexes);
                            const eventIndex = eventIndexResult.idx_name;
                            const eventIndexIsEstimated = eventIndexResult.isEstimated;

                            const groupBorderBottom = isLastInGroup
                              ? isLastGroup
                                ? 'none'
                                : '2px solid rgba(0, 0, 0, 0.12)'
                              : '1px solid rgba(0, 0, 0, 0.05)';

                            return (
                              <TableRow key={`${gIdx}-${vIdx}`} hover>
                                {isFirst && (
                                  <>
                                    <TableCell
                                      rowSpan={group.violations.length}
                                      sx={{
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'top',
                                        bgcolor: 'background.paper',
                                        borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                      }}
                                    >
                                      {dateKey || '-'}
                                    </TableCell>
                                    <TableCell
                                      rowSpan={group.violations.length}
                                      sx={{
                                        verticalAlign: 'top',
                                        bgcolor: 'background.paper',
                                        borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                      }}
                                    >
                                      <Chip
                                        label={
                                          group.isRetaseDay ? t('driverScore.retase', 'Retase') : t('driverScore.nonRetase', 'Non Retase')
                                        }
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.75rem',
                                          height: 22,
                                          bgcolor: group.isRetaseDay ? '#e8f5e9' : '#fff3e0',
                                          color: group.isRetaseDay ? '#2e7d32' : '#e65100'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      rowSpan={group.violations.length}
                                      sx={{
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'top',
                                        bgcolor: 'background.paper',
                                        borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                      }}
                                    >
                                      {group.vehicle}
                                    </TableCell>
                                    <TableCell
                                      rowSpan={group.violations.length}
                                      sx={{
                                        fontWeight: 500,
                                        verticalAlign: 'top',
                                        bgcolor: 'background.paper',
                                        borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                      }}
                                    >
                                      {group.scoreConfigName || '-'}
                                    </TableCell>
                                    <TableCell
                                      rowSpan={group.violations.length}
                                      sx={{
                                        verticalAlign: 'top',
                                        bgcolor: 'background.paper',
                                        borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                      }}
                                    >
                                      <Stack direction="column" spacing={0.25} alignItems="flex-start">
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Tooltip
                                            title={
                                              group.idxIsEstimated
                                                ? `${t('driverScore.indexWord', 'Index')} ${group.idxName} (${roundedLabel(group.idxRoundedDirection)} ${t('driverScore.fromScore', 'from score')} ${group.pctScore})`
                                                : ''
                                            }
                                            arrow
                                            disableHoverListener={!group.idxIsEstimated}
                                          >
                                            <Chip
                                              label={group.idxName || '-'}
                                              size="small"
                                              sx={{
                                                fontWeight: 'bold',
                                                minWidth: 36,
                                                ...getIndexColor(group.idxName)
                                              }}
                                            />
                                          </Tooltip>
                                          {group.pctScore != null && (
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              ({group.pctScore})
                                            </Typography>
                                          )}
                                        </Stack>
                                        {group.idxIsEstimated && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: '0.68rem',
                                              color: 'text.secondary',
                                              fontStyle: 'italic',
                                              lineHeight: 1.1
                                            }}
                                          >
                                            *{roundedLabel(group.idxRoundedDirection)}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </TableCell>
                                  </>
                                )}
                                <TableCell sx={{ fontWeight: 500, borderBottom: groupBorderBottom }}>
                                  {formatEventKey(ev.event_key, t)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 500, color: 'text.secondary', borderBottom: groupBorderBottom }}
                                >
                                  {ev.weight != null ? `${ev.weight}%` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, borderBottom: groupBorderBottom }}>
                                  {ev.event_count || 0}
                                </TableCell>
                                {showTotalRows && (
                                  <TableCell align="right" sx={{ fontWeight: 600, borderBottom: groupBorderBottom }}>
                                    {eventTotalRows !== null && eventTotalRows !== undefined ? eventTotalRows : ''}
                                  </TableCell>
                                )}
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: getScoreColor(calculatedEventScore),
                                    borderBottom: groupBorderBottom
                                  }}
                                >
                                  <Tooltip title={t('driverScore.clickToSeeFormula', 'Klik untuk lihat rumus & detail perhitungan')} arrow>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        setFormulaModal({
                                          eventKey: ev.event_key,
                                          eventCount: ev.event_count || 0,
                                          totalRows,
                                          weight: ev.weight || 0,
                                          score: calculatedEventScore,
                                          eventIndex,
                                          eventIndexIsEstimated,
                                          eventIndexRoundedDirection: eventIndexResult.roundedDirection,
                                          dateKey,
                                          vehicle: group.vehicle,
                                          scoreConfigName: group.scoreConfigName,
                                          isRetaseDay: group.isRetaseDay,
                                          groupViolations: group.violations,
                                          groupPctScore: group.pctScore,
                                          groupIdxName: group.idxName,
                                          scoreIndexes: group.scoreIndexes,
                                          vssTotalRows: group.vssTotalRows ?? data?.vssTotalRows ?? null,
                                          fallbackWaypoints: group.totalWaypoints
                                        })
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        borderBottom: '1px dashed currentColor',
                                        transition: 'background-color 0.15s',
                                        '&:hover': {
                                          bgcolor: 'rgba(0, 0, 0, 0.05)'
                                        }
                                      }}
                                    >
                                      {displayEventScore}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ borderBottom: groupBorderBottom }}>
                                  <Stack direction="column" alignItems="center" spacing={0.25}>
                                    <Tooltip
                                      title={
                                        eventIndexIsEstimated
                                          ? `${t('driverScore.indexWord', 'Index')} ${eventIndex} (${roundedLabel(eventIndexResult.roundedDirection)} ${t('driverScore.fromScore', 'from score')} ${(calculatedEventScore * 10).toFixed(2)})`
                                          : ''
                                      }
                                      arrow
                                      disableHoverListener={!eventIndexIsEstimated}
                                    >
                                      <Chip
                                        label={eventIndex || '-'}
                                        size="small"
                                        sx={{
                                          fontWeight: 'bold',
                                          minWidth: 32,
                                          ...getIndexColor(eventIndex)
                                        }}
                                      />
                                    </Tooltip>
                                    {eventIndexIsEstimated && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: '0.65rem',
                                          color: 'text.secondary',
                                          fontStyle: 'italic',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        *{roundedLabel(eventIndexResult.roundedDirection)}
                                      </Typography>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell align="center" sx={{ borderBottom: groupBorderBottom }}>
                                  {hasEvidence ? (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => setEvidenceModal({ eventKey: ev.event_key, source, dateKey })}
                                      sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: '2px 8px' }}
                                    >
                                      {t('driverScore.evidence', 'Evidence')}
                                    </Button>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                {isFirst && (
                                  <TableCell
                                    rowSpan={group.violations.length}
                                    align="center"
                                    sx={{
                                      verticalAlign: 'top',
                                      bgcolor: 'background.paper',
                                      borderBottom: isLastGroup ? 'none' : '2px solid rgba(0, 0, 0, 0.12)'
                                    }}
                                  >
                                    {retaseTripsForDate.length > 0 ||
                                    retaseDataForDate.geofences.length > 0 ||
                                    retaseDataForDate.transitPoints.length > 0 ? (
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => setRetaseModal({ dateKey })}
                                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: '2px 8px' }}
                                      >
                                        {t('driverScore.retase', 'Retase')} ({retaseTripsForDate.length})
                                      </Button>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          });
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </DetailDialog>

      <EvidenceDialog
        open={Boolean(evidenceModal)}
        onClose={() => setEvidenceModal(null)}
        eventKey={evidenceModal?.eventKey}
        source={evidenceModal?.source}
        groupedEvidence={
          evidenceModal
            ? evidenceModal.dateKey
              ? {
                  [evidenceModal.dateKey]:
                    (evidenceModal.source === 'GPS'
                      ? gpsEvidenceByKey[evidenceModal.eventKey]?.[evidenceModal.dateKey]
                      : vssEvidenceByKey[evidenceModal.eventKey]?.[evidenceModal.dateKey]) || []
                }
              : (evidenceModal.source === 'GPS' ? gpsEvidenceByKey[evidenceModal.eventKey] : vssEvidenceByKey[evidenceModal.eventKey]) || {}
            : {}
        }
      />

      {Boolean(retaseModal) &&
        (() => {
          const modalRetaseData = getRetaseForDate(retaseModal.dateKey);
          return (
            <RetaseDialog
              open={Boolean(retaseModal)}
              onClose={() => setRetaseModal(null)}
              date={retaseModal?.dateKey}
              retaseTrips={modalRetaseData.trips}
              geofences={modalRetaseData.geofences}
              transitPoints={modalRetaseData.transitPoints}
            />
          );
        })()}

      <FormulaDialog open={Boolean(formulaModal)} onClose={() => setFormulaModal(null)} formulaData={formulaModal} />
    </>
  );
}

DetailDriverScoreDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object
};
